import { useState } from 'react';
import {
  Monitor,
  CheckCircle2,
  XCircle,
  Loader2,
  MapPin,
  User,
  Clock,
  Globe,
  Smartphone,
} from 'lucide-react';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalBody,
  ResponsiveModalFooter,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
} from '@/components/ui/responsive-modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { useDeviceRegistration, DeviceApprovalRequest } from '@/hooks/useDeviceRegistration';
import { toast } from 'sonner';
import { ManagerPinDialog } from '@/components/ManagerPinDialog';
import { isRegisterLimitError } from './RegisterLimitDialog';

interface DeviceApprovalDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  request: (DeviceApprovalRequest & {
    requester?: { first_name: string | null; last_name: string | null };
  }) | null;
  onApproved?: () => void;
  onDenied?: () => void;
  onRegisterLimitError?: (errorMessage: string) => void;
}

export function DeviceApprovalDialog({
  open,
  onOpenChange,
  request,
  onApproved,
  onDenied,
  onRegisterLimitError,
}: DeviceApprovalDialogProps) {
  const { approveRequest, denyRequest } = useDeviceRegistration();

  const [deviceName, setDeviceName] = useState('');
  const [denyNotes, setDenyNotes] = useState('');
  const [showDenyForm, setShowDenyForm] = useState(false);
  const [showPinDialog, setShowPinDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState<'approve' | 'deny' | null>(null);

  if (!request) return null;

  const requesterName = request.requester
    ? `${request.requester.first_name || ''} ${request.requester.last_name || ''}`.trim()
    : 'Unknown User';

  const requestTime = new Date(request.created_at);
  const fingerprint = request.device_fingerprint;

  const handleApprove = () => {
    setPendingAction('approve');
    setShowPinDialog(true);
  };

  const handleDeny = () => {
    if (!showDenyForm) {
      setShowDenyForm(true);
      return;
    }
    setPendingAction('deny');
    setShowPinDialog(true);
  };

  const handlePinVerified = async () => {
    setShowPinDialog(false);

    if (pendingAction === 'approve') {
      try {
        await approveRequest.mutateAsync({
          requestId: request.id,
          deviceName: deviceName || request.requested_device_name || undefined,
        });
        toast.success('Device approved', {
          description: `The device has been registered for ${request.location?.name || 'the selected location'}.`,
        });
        onApproved?.();
        onOpenChange(false);
      } catch (error: any) {
        // Check if this is a register limit error
        if (isRegisterLimitError(error)) {
          onOpenChange(false);
          onRegisterLimitError?.(error.message);
        } else {
          toast.error('Failed to approve', {
            description: error.message || 'Please try again.',
          });
        }
      }
    } else if (pendingAction === 'deny') {
      try {
        await denyRequest.mutateAsync({
          requestId: request.id,
          notes: denyNotes || undefined,
        });
        toast.success('Request denied', {
          description: 'The device registration request has been denied.',
        });
        onDenied?.();
        onOpenChange(false);
      } catch (error: any) {
        toast.error('Failed to deny', {
          description: error.message || 'Please try again.',
        });
      }
    }

    setPendingAction(null);
  };

  const getDeviceTypeIcon = () => {
    if (fingerprint?.platform?.toLowerCase().includes('mac') ||
        fingerprint?.platform?.toLowerCase().includes('win') ||
        fingerprint?.platform?.toLowerCase().includes('linux')) {
      return <Monitor className="h-5 w-5" />;
    }
    if (fingerprint?.touchSupport) {
      return <Smartphone className="h-5 w-5" />;
    }
    return <Globe className="h-5 w-5" />;
  };

  return (
    <>
      <ResponsiveModal open={open && !showPinDialog} onOpenChange={onOpenChange}>
        <ResponsiveModalContent maxWidth="md">
          <ResponsiveModalHeader>
            <div className="mx-auto w-12 h-12 rounded-full bg-amber-500/10 flex items-center justify-center mb-2">
              <Monitor className="h-6 w-6 text-amber-600" />
            </div>
            <ResponsiveModalTitle className="text-center">
              Device Registration Request
            </ResponsiveModalTitle>
            <ResponsiveModalDescription className="text-center">
              Review and approve or deny this device registration request.
            </ResponsiveModalDescription>
          </ResponsiveModalHeader>

          <ResponsiveModalBody className="space-y-4">
            {/* Requester Info */}
            <div className="bg-muted/50 rounded-xl border p-4 space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium">{requesterName}</p>
                  <p className="text-sm text-muted-foreground">Requesting device access</p>
                </div>
              </div>

              <div className="flex items-center gap-2 text-sm">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                <span>Requesting access to: <strong>{request.location?.name}</strong></span>
              </div>

              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Clock className="h-4 w-4" />
                <span>
                  Requested {requestTime.toLocaleTimeString()} on {requestTime.toLocaleDateString()}
                </span>
              </div>
            </div>

            {/* Device Info */}
            <div className="space-y-2">
              <h4 className="text-sm font-medium">Device Information</h4>
              <div className="bg-card rounded-xl border p-4 space-y-3">
                <div className="flex items-center gap-3">
                  {getDeviceTypeIcon()}
                  <div className="flex-1">
                    <p className="font-medium">
                      {request.requested_device_name || 'Unnamed Device'}
                    </p>
                    <p className="text-xs text-muted-foreground font-mono">
                      {request.device_id.slice(0, 32)}...
                    </p>
                  </div>
                  <Badge variant="secondary">
                    {fingerprint?.platform || 'Unknown'}
                  </Badge>
                </div>

                {fingerprint && (
                  <div className="grid grid-cols-2 gap-2 text-xs border-t pt-3">
                    <div>
                      <span className="text-muted-foreground">Browser: </span>
                      <span className="font-medium">
                        {fingerprint.userAgent?.includes('Chrome') ? 'Chrome' :
                         fingerprint.userAgent?.includes('Firefox') ? 'Firefox' :
                         fingerprint.userAgent?.includes('Safari') ? 'Safari' : 'Other'}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Screen: </span>
                      <span className="font-medium">{fingerprint.screenResolution}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Timezone: </span>
                      <span className="font-medium">{fingerprint.timezone}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Touch: </span>
                      <span className="font-medium">{fingerprint.touchSupport ? 'Yes' : 'No'}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Device Name (editable) */}
            <div className="space-y-2">
              <Label htmlFor="deviceName">Device Name</Label>
              <Input
                id="deviceName"
                placeholder={request.requested_device_name || 'Enter a name for this device'}
                value={deviceName}
                onChange={(e) => setDeviceName(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                You can change the device name before approving.
              </p>
            </div>

            {/* Deny Form */}
            {showDenyForm && (
              <div className="space-y-2 border-t pt-4">
                <Label htmlFor="denyNotes">Reason for Denial (optional)</Label>
                <Textarea
                  id="denyNotes"
                  placeholder="Enter a reason for denying this request..."
                  value={denyNotes}
                  onChange={(e) => setDenyNotes(e.target.value)}
                  rows={3}
                />
              </div>
            )}
          </ResponsiveModalBody>

          <ResponsiveModalFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              className="flex-1 gap-2 text-red-600 hover:text-red-600 hover:bg-red-50"
              onClick={handleDeny}
              disabled={approveRequest.isPending || denyRequest.isPending}
            >
              {denyRequest.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <XCircle className="h-4 w-4" />
              )}
              {showDenyForm ? 'Confirm Deny' : 'Deny'}
            </Button>
            <Button
              variant="blaze"
              className="flex-1 gap-2"
              onClick={handleApprove}
              disabled={approveRequest.isPending || denyRequest.isPending}
            >
              {approveRequest.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <CheckCircle2 className="h-4 w-4" />
              )}
              Approve Device
            </Button>
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>

      <ManagerPinDialog
        open={showPinDialog}
        onOpenChange={setShowPinDialog}
        onVerified={handlePinVerified}
        title={pendingAction === 'approve' ? 'Approve Device' : 'Deny Request'}
        description={
          pendingAction === 'approve'
            ? `Enter your PIN to approve this device for ${request.location?.name}.`
            : 'Enter your PIN to deny this device registration request.'
        }
      />
    </>
  );
}
